"use client";

import * as CookieConsent from "vanilla-cookieconsent";

import {
  CONSENT_CATEGORIES,
  CONSENT_CHANGED_EVENT,
  type ConsentCategory,
} from "./categories";

type ConsentChangedDetail = {
  acceptedCategories: string[];
};

const COOKIE_PATHS = ["/"];
const STATIC_COOKIE_NAMES = [
  "CookieConsent",
  "CookieConsentBulkTicket",
  "CookieConsentPolicy",
  "chakra-ui-color-mode",
  "email",
  "has_password",
];
const ANALYTICS_COOKIE_NAMES = ["_ga", "_gid", "_gat", "_clck", "_clsk"];
const MARKETING_COOKIE_NAMES = [
  "_fbp",
  "_fbc",
  "_rdt_uuid",
  "rdt_uuid",
  "alpha_collab_ref",
  "alpha_collab_visitor_id",
  "alpha_collab_visit_id",
];
const LEGACY_LOCAL_STORAGE_KEYS = [
  "CookieConsent",
  "theme",
  "locale",
  "chakra-ui-color-mode",
  "email",
  "has_password",
];

const getHostnameDomainVariants = () => {
  if (typeof window === "undefined") return [undefined];
  const hostname = window.location.hostname;
  const variants = new Set<string | undefined>([undefined, hostname]);

  if (hostname === "alphastrategy.io" || hostname.endsWith(".alphastrategy.io")) {
    variants.add(".alphastrategy.io");
    variants.add("alphastrategy.io");
  }

  if (hostname.startsWith("www.")) {
    variants.add(hostname.slice(4));
    variants.add(`.${hostname.slice(4)}`);
  }

  return Array.from(variants);
};

const safeLocalStorageRemove = (key: string) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Best-effort cleanup only.
  }
};

const safeLocalStorageRemoveMatching = (pattern: RegExp) => {
  if (typeof window === "undefined") return;
  try {
    const keys = Array.from({ length: window.localStorage.length })
      .map((_, index) => window.localStorage.key(index))
      .filter((key): key is string => Boolean(key));
    keys.filter((key) => pattern.test(key)).forEach(safeLocalStorageRemove);
  } catch {
    // Best-effort cleanup only.
  }
};

const getCookieNames = () => {
  if (typeof document === "undefined") return [];
  return document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter(Boolean);
};

const matchingCookieNames = (patterns: Array<string | RegExp>) => {
  const names = getCookieNames();
  return names.filter((name) =>
    patterns.some((pattern) =>
      typeof pattern === "string" ? name === pattern : pattern.test(name)
    )
  );
};

export const expireCookie = (name: string) => {
  if (typeof document === "undefined") return;
  const encodedName = encodeURIComponent(name);
  const domains = getHostnameDomainVariants();

  for (const path of COOKIE_PATHS) {
    for (const domain of domains) {
      const parts = [
        `${encodedName}=`,
        `path=${path}`,
        "expires=Thu, 01 Jan 1970 00:00:00 GMT",
        "max-age=0",
        "samesite=lax",
      ];
      if (domain) parts.push(`domain=${domain}`);
      if (window.location.protocol === "https:") parts.push("secure");
      try {
        document.cookie = parts.join("; ");
      } catch {
        // Keep deleting remaining variants.
      }
    }
  }
};

export const expireCookies = (patterns: Array<string | RegExp>) => {
  const names = new Set([
    ...patterns.filter((pattern): pattern is string => typeof pattern === "string"),
    ...matchingCookieNames(patterns),
  ]);
  names.forEach(expireCookie);
};

export const clearLegacyCookies = () => {
  expireCookies([
    ...STATIC_COOKIE_NAMES,
    /^CookieConsent/i,
    new RegExp("^Cookie" + "bot", "i"),
  ]);
  LEGACY_LOCAL_STORAGE_KEYS.forEach(safeLocalStorageRemove);
};

export const clearAnalyticsCookies = () => {
  expireCookies([
    ...ANALYTICS_COOKIE_NAMES,
    /^_ga_/,
    /^_gat/,
    /^_cl/,
    "CLID",
    "ANONCHK",
    "MR",
    "MUID",
    "SM",
  ]);
};

export const clearMarketingCookies = () => {
  expireCookies([...MARKETING_COOKIE_NAMES, /^alpha_collab_/]);
  MARKETING_COOKIE_NAMES.forEach(safeLocalStorageRemove);
  safeLocalStorageRemoveMatching(/^alpha_collab_/);
  // Partner attribution also used the tab's session storage (the pending ref, before 2026-09-30).
  try {
    ["alpha_collab_pending_attribution", "alpha_collab_tracked_landing_keys"].forEach((key) =>
      window.sessionStorage.removeItem(key)
    );
  } catch {
    // Best-effort cleanup only.
  }
};

export const getAcceptedConsentCategories = () => {
  if (typeof window === "undefined") return [];
  try {
    return CookieConsent.getUserPreferences().acceptedCategories;
  } catch {
    return [];
  }
};

export const hasConsentCategory = (category: ConsentCategory) => {
  if (typeof window === "undefined") return false;
  try {
    return CookieConsent.acceptedCategory(category);
  } catch {
    return false;
  }
};

export const hasStoredConsent = () => {
  if (typeof window === "undefined") return false;
  try {
    return CookieConsent.validConsent();
  } catch {
    return false;
  }
};

export const dispatchConsentChanged = () => {
  if (typeof window === "undefined") return;
  const detail: ConsentChangedDetail = {
    acceptedCategories: getAcceptedConsentCategories(),
  };
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail }));
};

export const onConsentChanged = (
  listener: (detail: ConsentChangedDetail) => void
) => {
  if (typeof window === "undefined") return () => undefined;
  const handler = (event: Event) => {
    listener((event as CustomEvent<ConsentChangedDetail>).detail);
  };
  window.addEventListener(CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
};

const writeCookie = (name: string, value: string, maxAge: number) => {
  if (typeof document === "undefined") return;
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${maxAge}`,
    "samesite=lax",
  ];
  if (window.location.protocol === "https:") parts.push("secure");
  document.cookie = parts.join("; ");
};

const ONE_YEAR = 60 * 60 * 24 * 365;

// Theme and language are only written when the reader picks one (the theme toggle, the language
// menu): an interface choice they asked for, which makes them necessary cookies that need no
// consent (2026-09-30). Nothing may set them on its own.
export const setThemePreferenceCookie = (theme: "light" | "dark" | "system") => {
  writeCookie("theme", theme, ONE_YEAR);
};

export const setLocalePreferenceCookie = (locale: string) => {
  writeCookie("locale", locale, ONE_YEAR);
};

export const showCookiePreferences = () => {
  if (typeof window === "undefined") return;
  try {
    CookieConsent.showPreferences();
  } catch {
    // If the provider has not initialized yet, do nothing.
  }
};
